import { METHOD_COLORS, toFeatureCollection } from './gis-types';


// ── USGS Cape Cod Bay Geophysical Survey (2019-002-FA / 2019-034-FA) ──
// Source: https://doi.org/10.5066/P99DR4PN
// Real data loaded from public/data/*.geojson (converted from USGS shapefiles + CSV)
export const DEMO_LAYERS = [
  {
    id: 'l1', name: 'Multibeam Sonar Tracklines (T20-P)', method: 'Seismic Reflection', date: '2019-08-15',
    campaign: 'USGS Cape Cod Bay 2019', visible: true, opacity: 100, anomalies: 0,
    lines: [], soundings: [], geojsonUrl: `${process.env.PUBLIC_URL}/data/tracklines.geojson`,
  },
  {
    id: 'l2', name: 'Sediment Samples (Grain Size)', method: 'Geology', date: '2019-09-17',
    campaign: 'USGS Cape Cod Bay 2019', visible: true, opacity: 100, anomalies: 0,
    lines: [], soundings: [], geojsonUrl: `${process.env.PUBLIC_URL}/data/samples.geojson`,
  },
  {
    id: 'l3', name: 'Sound Velocity Profiles (MVP/SVP)', method: 'Borehole', date: '2019-08-10',
    campaign: 'USGS Cape Cod Bay 2019', visible: true, opacity: 100, anomalies: 0,
    lines: [], soundings: [], geojsonUrl: `${process.env.PUBLIC_URL}/data/svp.geojson`,
  },
];

export const DEMO_ANOMALIES = [
  { id: 'an1', lat: 41.99, lon: -70.16, label: 'A1', type: 'Subsurface channel', desc: 'Paleo-channel identified in seismic profile. Incised 15-25m below seafloor. Possible glacial outwash.', severity: 'high' },
  { id: 'an2', lat: 42.01, lon: -70.19, label: 'A2', type: 'Gas-charged sediment', desc: 'Acoustic blanking in chirp data. Possible shallow gas at 5-8m depth. Silty clay (50% clay).', severity: 'medium' },
  { id: 'an3', lat: 41.97, lon: -70.12, label: 'A3', type: 'Hard substrate', desc: 'High backscatter + coarse sediment. Boulder/cobble lag deposit. Gravel present.', severity: 'low' },
];

// AI-suggested line
export const AI_SUGGESTED_LINE = {
  type: 'Feature',
  properties: { name: 'AI: suggest additional chirp line' },
  geometry: {
    type: 'LineString',
    coordinates: [[-70.25, 41.95], [-70.05, 41.95]],
  },
};

// ── Helpers ──
export function buildLineGeoJSON(layers) {
  const features = [];
  layers.forEach(layer => {
    if (!layer.visible || !layer.lines) return;
    layer.lines.forEach(line => {
      features.push({
        type: 'Feature',
        properties: {
          id: line.id, name: line.name, layerId: layer.id,
          method: layer.method, color: METHOD_COLORS[layer.method] || '#8b949e',
          electrodes: line.electrodes, length: line.length,
        },
        geometry: {
          type: 'LineString',
          coordinates: [line.start, line.end],
        },
      });
    });
  });
  return toFeatureCollection(features);
}

export function buildElectrodeGeoJSON(layers) {
  const features = [];
  layers.forEach(layer => {
    if (!layer.visible || !layer.lines) return;
    layer.lines.forEach(line => {
      const count = Math.min(line.electrodes || 8, 20); // cap for perf
      for (let i = 0; i <= count; i++) {
        const t = i / count;
        features.push({
          type: 'Feature',
          properties: { lineId: line.id, color: METHOD_COLORS[layer.method] || '#8b949e' },
          geometry: {
            type: 'Point',
            coordinates: [
              line.start[0] + (line.end[0] - line.start[0]) * t,
              line.start[1] + (line.end[1] - line.start[1]) * t,
            ],
          },
        });
      }
    });
  });
  return toFeatureCollection(features);
}

export function buildSoundingGeoJSON(layers) {
  const features = [];
  layers.forEach(layer => {
    if (!layer.visible || !layer.soundings) return;
    layer.soundings.forEach(s => {
      features.push({
        type: 'Feature',
        properties: {
          id: s.id, label: s.label, method: layer.method, layerId: layer.id,
          color: METHOD_COLORS[layer.method] || '#8b949e',
          isBorehole: layer.method === 'Borehole',
        },
        geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      });
    });
  });
  return toFeatureCollection(features);
}

export function buildAnomalyGeoJSON(anomalies) {
  return toFeatureCollection(anomalies.map(a => ({
    type: 'Feature',
    properties: {
      id: a.id, label: a.label, type: a.type, desc: a.desc, severity: a.severity,
      color: a.severity === 'high' ? '#f87171' : a.severity === 'medium' ? '#fbbf24' : '#00d4ff',
    },
    geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
  })));
}

export function buildLineLabelGeoJSON(layers) {
  const features = [];
  layers.forEach(layer => {
    if (!layer.visible || !layer.lines) return;
    layer.lines.forEach(line => {
      features.push({
        type: 'Feature',
        properties: { name: line.name, color: METHOD_COLORS[layer.method] || '#8b949e' },
        geometry: {
          type: 'Point',
          coordinates: [line.start[0] - 0.0005, (line.start[1] + line.end[1]) / 2],
        },
      });
    });
  });
  return toFeatureCollection(features);
}

// ── Terra Draw mode mapping (UI mode ↔ terra-draw mode) ──
export const DRAW_MODE_MAP = { point: 'point', line: 'linestring', polygon: 'polygon', rectangle: 'rectangle', circle: 'circle', freehand: 'freehand', select: 'select' };
export const DRAW_MODE_REVERSE = { point: 'point', linestring: 'line', polygon: 'polygon', rectangle: 'rectangle', circle: 'circle', freehand: 'freehand', select: 'select' };

// ── Known project locations (geocoded from location text) ──
export const PROJECT_LOCATIONS = {
  'Cape Cod Bay': { lat: 41.99, lon: -70.15, zoom: 10 },
  'Massachusetts': { lat: 41.99, lon: -70.15, zoom: 10 },
  'Ellsworth': { lat: 44.145, lon: -103.09, zoom: 12 },
  'South Dakota': { lat: 44.145, lon: -103.09, zoom: 12 },
};

// Convert a `#rrggbb` token to an rgba() string at the given alpha — used for
// translucent overlay backgrounds that need to track the active theme's bg0.
export function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getProjectCenter(project) {
  if (!project) return null;
  // Check extent field first (GeoJSON bbox)
  if (project.extent) {
    const ext = project.extent;
    if (ext.center) return { lat: ext.center[1], lon: ext.center[0], zoom: ext.zoom || 12 };
    if (ext.bbox) {
      const [minLon, minLat, maxLon, maxLat] = ext.bbox;
      return { lat: (minLat + maxLat) / 2, lon: (minLon + maxLon) / 2, zoom: 11 };
    }
  }
  // Fall back to matching location text
  if (project.location) {
    for (const [key, coords] of Object.entries(PROJECT_LOCATIONS)) {
      if (project.location.toLowerCase().includes(key.toLowerCase())) {
        return coords;
      }
    }
  }
  return null;
}

// Explode Multi* geometries into simple geometries for MapLibre compatibility
// (also normalises the legacy 'PolyLine' type). Mutates and returns the input.
export function explodeMultiGeometries(geojson) {
  if (!geojson?.features) return geojson;
  const exploded = [];
  geojson.features.forEach(f => {
    if (!f.geometry) return;
    if (f.geometry.type === 'PolyLine') f.geometry.type = 'LineString';
    if (f.geometry.type === 'MultiPoint') {
      f.geometry.coordinates.forEach(coord => {
        exploded.push({ ...f, geometry: { type: 'Point', coordinates: coord } });
      });
    } else if (f.geometry.type === 'MultiLineString') {
      f.geometry.coordinates.forEach(coords => {
        exploded.push({ ...f, geometry: { type: 'LineString', coordinates: coords } });
      });
    } else {
      exploded.push(f);
    }
  });
  geojson.features = exploded;
  return geojson;
}

// Per-layer stats of the active Z field for imported XYZ layers.
export function computeXyzStats(importedLayers) {
  const out = {};
  importedLayers.forEach(l => {
    if (!l.xyz || !l.activeZ) return;
    let min = Infinity, max = -Infinity, sum = 0, sumSq = 0, count = 0;
    l.geojson.features.forEach(f => {
      if (f.properties?._excluded) return;
      const v = f.properties?.[l.activeZ];
      if (typeof v !== 'number' || !isFinite(v)) return;
      count++;
      sum += v; sumSq += v * v;
      if (v < min) min = v;
      if (v > max) max = v;
    });
    if (count === 0) return;
    const mean = sum / count;
    out[l.id] = { min, max, mean, std: Math.sqrt(Math.max(0, sumSq / count - mean * mean)), count };
  });
  return out;
}

